import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, filter, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { HttpClient } from '@angular/common/http';
import { UserLoginModel } from '../models/user-login.model';
import { IUnifiedSettingsDistrictModel, IUnifiedSettingsUserModel, UnifiedSettingsDistrictModel, UnifiedSettingsUserModel } from '../models/unified-settings.model';
import * as _ from 'lodash-es';
import { Application } from 'src/portaljs/constants/application';
import { ConsoleLogger } from '../shared/console-logger';


@Injectable({ providedIn: 'root' })
export class UnifiedSettingsService implements OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  private _unSettings: BehaviorSubject<UnifiedSettingsUserModel> = new BehaviorSubject<UnifiedSettingsUserModel>(undefined);
  private unSettings$: Observable<UnifiedSettingsUserModel> = this._unSettings.asObservable();
  private _districtSettings: BehaviorSubject<UnifiedSettingsDistrictModel> = new BehaviorSubject<UnifiedSettingsDistrictModel>(undefined);
  private districtSettings$: Observable<UnifiedSettingsDistrictModel> = this._districtSettings.asObservable();


  constructor( private http: HttpClient, private envService: EnvironmentService) {
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }


  public fetchUnifiedDistrictSettings(districtAppId: string): Observable<UnifiedSettingsDistrictModel> {
    if (!this.envService.isFUN_ENABLED()) {
      return of(undefined);
    }

    //For now, just have all site logos get returned for the district, regardless of user and what sites they have access to
    const url = this.envService.getPortalBackendUrl() + '/district/' + districtAppId;
    return this.http.get<IUnifiedSettingsDistrictModel | { error: { status: number; message: string; requestId: string } }>(url,
        { withCredentials: true})
        .pipe(
          map((response) => {
            if (response && 'error' in response && response.error) {
              throw response.error;
            }
            return response as IUnifiedSettingsDistrictModel;
          }),
          filter(s => !!s),
          switchMap((settings: IUnifiedSettingsDistrictModel) => {
            const dataUrl = settings?.districtHomePage?.dataUrl;
            if (dataUrl && settings?.districtHomePage?.enabled) {
              return this.http.get(dataUrl, { responseType: 'text' as 'json' })
                .pipe(
                  map((data: any) => {
                    settings.districtHomePage.data = data;
                    return settings;
                  }),
                  catchError((error) => {
                    ConsoleLogger.error('Error fetching district home page data:', error);
                    return of(settings);
                  })
                );
            }
            return of(settings);
          }),
          map((settings: IUnifiedSettingsDistrictModel) => {
            const usdm = new UnifiedSettingsDistrictModel(settings);
            this.setUnifiedDistrictSettings(usdm);
            return usdm;
          }),
          catchError((error) => {
            if (error?.status === 404) {
              this.setUnifiedDistrictSettings(undefined);
              ConsoleLogger.info(`DistrictSettings not found for user (requestId: ${error?.requestId})`);
            } else {
              ConsoleLogger.error(`Error fetching district settings (requestId: ${error?.requestId}):`, error);
            }
            return of(undefined);
          })
        );
  }

  public getUnifiedDistrictSettings(): UnifiedSettingsDistrictModel {
    return _.cloneDeep(this._districtSettings.getValue());
  }
  public getUnifiedDistrictSettings$(): Observable<UnifiedSettingsDistrictModel> {
    return this.districtSettings$;
  }
  public setUnifiedDistrictSettings(settings: UnifiedSettingsDistrictModel) {
    this._districtSettings.next(settings);
  }


  public fetchPortalUserSetting(ulm: UserLoginModel, paths: string[] = []): Observable<UnifiedSettingsUserModel> {
    if (!this.envService.isFUN_ENABLED() || !ulm.doesUserHaveAccessToSettings()) {
      return of(undefined);
    }

    const params = {
      access_token: localStorage.getItem('accessToken')
    };

    const joinedPath = paths.join('/') + '?includeGlobal=true';
    return this.http.get<IUnifiedSettingsUserModel | { error: { status: number; message: string; requestId: string } }>(this.envService.getPortalBackendUrl() + '/user/unsettings/portal/' + ulm.getUserGuid() + '/' + joinedPath,
        { params, withCredentials: true})
        .pipe(
          map((response) => {
            if (response && 'error' in response && response.error) {
              throw response.error;
            }
            return response as IUnifiedSettingsUserModel;
          }),
          filter(s => !!s),
          map((settings: IUnifiedSettingsUserModel) => {
            const unsm = new UnifiedSettingsUserModel(settings);
            this.setUnifiedSettings(unsm);
            return unsm;
          }),
          catchError((error) => {
            if (error?.status === 404) {
              this.setUnifiedSettings(undefined);
              ConsoleLogger.info(`Settings not found for user (requestId: ${error?.requestId})`);
            } else {
              ConsoleLogger.error(`Error fetching portal user settings (requestId: ${error?.requestId}):`, error);
            }
            return of(undefined);
          })
        );
  }


  public getUnifiedSettings(): UnifiedSettingsUserModel {
    return _.cloneDeep(this._unSettings.getValue());
  }
  public getUnifiedSettings$(): Observable<UnifiedSettingsUserModel> {
    return this.unSettings$;
  }
  public setUnifiedSettings(settings: UnifiedSettingsUserModel) {
    this._unSettings.next(settings);
  }



  /** -------------------------------------------------------------------- */
  /** Because of CORS, we can't issue a PATCH/PUT/POST, so for now, only a GET is used for a single value update */
  private patchPortalSettings(ulm: UserLoginModel, key: string, value: string): Observable<any> {
    if (!this.envService.isFUN_ENABLED() || !ulm.doesUserHaveAccessToSettings() || !key || !value) {
      return of(undefined);
    }

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return of(undefined);
    }

    const params = {
      access_token: accessToken
    };

    return this.http.get<any>(this.envService.getPortalBackendUrl() + '/user/unsettings/portal/' + ulm.getUserGuid() + '/' + key + '/' + value,
      { params, withCredentials: true})
      .pipe(
        catchError((error) => {
          ConsoleLogger.error('Error patching last app', error);
          return of(undefined);
        })
      );
  }


  /** -------------------------------------------------------------------- */
  public updateLastApp(ulm: UserLoginModel, app: Application): Observable<void> {
    if (app === Application.UNIFIED_SETTINGS || app === Application.PORTAL) { // Don't count UNS as the last app
      return of(undefined);
    }

    if (!this.getUnifiedSettings()?.isTrackingLastApp()) {
      ConsoleLogger.debug('UnifiedSettingsService.updateLastApp: Not tracking last app');
      return of(undefined);
    }

    return this.patchPortalSettings(ulm, 'lastApp', app )
      .pipe(
        tap(() => {
          const currSettings = this.getUnifiedSettings();
          if (currSettings) {
            currSettings.setLastApp(app);
            this.setUnifiedSettings(currSettings);
          }
        })
      );
  }


  /** -------------------------------------------------------------------- */
  public updateLastSite(ulm: UserLoginModel, siteGuid: string): Observable<any> {
    if (!this.getUnifiedSettings()?.isTrackingLastSite()) {
      ConsoleLogger.debug('UnifiedSettingsService.updateLastSite: Not tracking last site');
      return of(undefined);
    }

    return this.patchPortalSettings(ulm, 'lastSite', siteGuid || 'district' )
      .pipe(
        tap(() => {
          const currSettings = this.getUnifiedSettings();
          if (currSettings) {
            currSettings.setLastSite(siteGuid);
            this.setUnifiedSettings(currSettings);
          }
        })
      );
  }

  public isITAMEnabled(): boolean {
    try{
      const scopes: any = this.getUnifiedDistrictSettings()?.getScopes();
      return scopes?.portal?.['ml-itam'] === true;
    } catch (e) {
      ConsoleLogger.debug('Error parsing ITAM scopes from district settings:', e);
      return false;
    }
  }

  public getSideCarScope(): boolean | undefined {
    try{
      const scopes: any = this.getUnifiedDistrictSettings()?.getScopes();
      return scopes?.portal?.['ai-sidecar'];
    } catch (e) {
      ConsoleLogger.debug('Error parsing AI SideCar scopes from district settings:', e);
      return undefined;
    }
  }
}
